import React, { useState, useEffect } from 'react';
import {
  FaCertificate, FaAward, FaGlobe, FaCheckCircle, FaStar,
  FaDownload, FaArrowRight, FaUserGraduate, FaChartLine,
  FaShieldAlt, FaBook, FaCalendarAlt, FaClock, FaUsers,
  FaLaptopCode, FaFileAlt, FaSearch, FaFilter, FaInfoCircle,
  FaGraduationCap, FaTrophy, FaRocket, FaLightbulb
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Certifications = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [visibleSections, setVisibleSections] = useState(new Set());

  // Intersection Observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => new Set([...prev, entry.target.dataset.section]));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    document.querySelectorAll('[data-section]').forEach(section => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const certifications = [
    {
      id: 1,
      title: 'ISO/IEC 27001 Lead Implementer',
      // organism: 'PECB',
      category: 'Sécurité de l\'Information',
      level: 'Avancé',
      duration: '5 jours',
      price: '1 200 000 FCFA',
      description: 'Certification internationale en management de la sécurité de l\'information selon ISO/IEC 27001.',
      recognition: 'International',
      examen: 'Inclus',
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&q=80',
      benefits: [
        'Reconnaissance internationale',
        'Expertise en sécurité ISO 27001',
        // 'Certification PECB officielle',
        'Support de cours inclus'
      ],
      prerequisites: 'Connaissance de base en sécurité de l\'information',
      color: '#dc3545'
    },
    {
      id: 2,
      title: 'ISO 9001 Lead Auditor',
      // organism: 'PECB',
      category: 'Qualité',
      level: 'Avancé',
      duration: '5 jours',
      price: '950 000 FCFA',
      description: 'Formation pour devenir auditeur certifié des systèmes de management de la qualité ISO 9001.',
      recognition: 'International',
      examen: 'Inclus',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&q=80',
      benefits: [
        'Certification Lead Auditor',
        'Reconnaissance mondiale',
        'Compétences en audit qualité',
        'Cas pratiques inclus'
      ],
      prerequisites: 'Expérience en management de la qualité recommandée',
      color: '#198754'
    },
    {
      id: 3,
      title: 'ISO 22301 Lead Implementer',
      // organism: 'PECB',
      category: 'Continuité d\'Activité',
      level: 'Avancé',
      duration: '5 jours',
      price: '1 100 000 FCFA',
      description: 'Certification en management de la continuité d\'activité selon la norme ISO 22301.',
      recognition: 'International',
      examen: 'Inclus',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80',
      benefits: [
        'Expert en continuité d\'activité',
        'Certification internationale',
        'Plan de continuité opérationnel',
        'Matériel pédagogique complet'
      ],
      prerequisites: 'Connaissance en gestion des risques',
      color: '#fd7e14'
    },
    {
      id: 4,
      title: 'ISO 37001 Lead Implementer',
      // organism: 'PECB',
      category: 'Anti-Corruption',
      level: 'Avancé',
      duration: '5 jours',
      price: '1 050 000 FCFA',
      description: 'Formation certifiante sur les systèmes de management anti-corruption ISO 37001.',
      recognition: 'International',
      examen: 'Inclus',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&q=80',
      benefits: [
        'Expert anti-corruption certifié',
        'Conformité internationale',
        'Compétences en gouvernance',
        // 'Certification PECB'
      ],
      prerequisites: 'Connaissance en conformité recommandée',
      color: '#20c997'
    },
    {
      id: 5,
      title: 'ISO 14001 Lead Auditor',
      // organism: 'PECB',
      category: 'Environnement',
      level: 'Avancé',
      duration: '5 jours',
      price: '900 000 FCFA',
      description: 'Devenez auditeur certifié des systèmes de management environnemental ISO 14001.',
      recognition: 'International',
      examen: 'Inclus',
      image: 'https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?w=600&q=80',
      benefits: [
        'Auditeur environnemental certifié',
        'Norme ISO 14001',
        'Compétences en développement durable',
        'Certificat international'
      ],
      prerequisites: 'Bases en management environnemental',
      color: '#10b981'
    },
    {
      id: 6,
      title: 'ISO 45001 Lead Implementer',
      // organism: 'PECB',
      category: 'Santé & Sécurité',
      level: 'Avancé',
      duration: '5 jours',
      price: '980 000 FCFA',
      description: 'Certification en management de la santé et sécurité au travail selon ISO 45001.',
      recognition: 'International',
      examen: 'Inclus',
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&q=80',
      benefits: [
        'Expert santé-sécurité certifié',
        'Conformité ISO 45001',
        'Prévention des risques',
        'Reconnaissance mondiale'
      ],
      prerequisites: 'Expérience en HSE recommandée',
      color: '#8b5cf6'
    }
  ];

  const categories = [...new Set(certifications.map(c => c.category))];
  const levels = [...new Set(certifications.map(c => c.level))];

  const filteredCertifications = certifications.filter(cert => {
    const matchSearch = cert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       cert.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory = selectedCategory === 'all' || cert.category === selectedCategory;
    const matchLevel = selectedLevel === 'all' || cert.level === selectedLevel;
    return matchSearch && matchCategory && matchLevel;
  });

  const stats = [
    { icon: FaCertificate, value: '25+', label: 'Certifications', color: '#fd7e14' },
    { icon: FaGlobe, value: '100%', label: 'Reconnaissance', color: '#198754' },
    { icon: FaUsers, value: '500+', label: 'Certifiés', color: '#20c997' },
    { icon: FaTrophy, value: '98%', label: 'Réussite', color: '#dc3545' }
  ];

  const advantages = [
    {
      icon: FaShieldAlt,
      title: 'Reconnaissance Internationale',
      // description: 'Certifications PECB reconnues dans plus de 100 pays à travers le monde',
      color: '#fd7e14'
    },
    {
      icon: FaUserGraduate,
      title: 'Formateurs Experts',
      description: 'Instructeurs certifiés et accrédités avec une vaste expérience pratique',
      color: '#198754'
    },
    {
      icon: FaRocket,
      title: 'Évolution de Carrière',
      description: 'Boostez votre carrière avec des certifications valorisées par les employeurs',
      color: '#20c997'
    },
    {
      icon: FaBook,
      title: 'Support Complet',
      description: 'Documentation complète, révisions et suivi post-formation inclus',
      color: '#8b5cf6'
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Inscription',
      description: 'Choisissez votre certification et inscrivez-vous en ligne',
      icon: FaUserGraduate
    },
    {
      number: '02',
      title: 'Formation',
      description: 'Suivez la formation intensive de 5 jours avec nos experts',
      icon: FaBook
    },
    {
      number: '03',
      title: 'Examen',
      // description: 'Passez l\'examen officiel de certification PECB',
      icon: FaFileAlt
    },
    {
      number: '04',
      title: 'Certification',
      description: 'Recevez votre certificat international reconnu',
      icon: FaAward
    }
  ];

  return (
    <>
    <Header />
    <div className="certifications-wrapper">
      {/* Hero Section */}
      <section className="certifications-hero">
        <div className="hero-overlay"></div>
        <div className="certifications-container">
          <div className="hero-content">
            <div className="hero-badge">
              <FaAward className="badge-icon-spin" />
              {/* <span>Partenaire officiel PECB</span> */}
            </div>

            <h1 className="hero-title">
              Certifications Internationales <br />
              <span className="gradient-text"></span>
            </h1>

            <p className="hero-description">
              Obtenez des certifications reconnues mondialement avec CINEF, 
              {/* partenaire officiel PECB pour l'Afrique de l'Ouest. Développez votre  */}
              expertise et boostez votre carrière avec nos formations certifiantes.
            </p>

            <div className="hero-actions">
              <button className="btn-primary-hero">
                Voir les Certifications
                <FaArrowRight />
              </button>
              <button className="btn-secondary-hero">
                <FaDownload />
                Télécharger le Catalogue
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="certifications-container">
          <div className="stats-grid">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="stat-card" style={{ '--stat-color': stat.color }}>
                  <div className="stat-icon">
                    <Icon />
                  </div>
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section
        className={`advantages-section ${visibleSections.has('advantages') ? 'visible' : ''}`}
        data-section="advantages"
      >
        <div className="certifications-container">
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-dot"></span>
              <span>Pourquoi choisir nos certifications</span>
            </div>
            <h2 className="section-title" style={{ textAlign: "center", display: "block" }}>
            Des Certifications qui{" "}
            <span className="highlight-text">font la différence</span>
          </h2>
          </div>

          <div className="advantages-grid" style={{
            
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "1.5rem",
                    
           }}>
            {advantages.map((advantage, index) => {
              const Icon = advantage.icon;
              return (
                <div key={index} className="advantage-card" style={{ '--advantage-color': advantage.color }}>
                  <div className="advantage-icon">
                    <Icon />
                  </div>
                  <h3>{advantage.title}</h3>
                  <p>{advantage.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications List Section */}
      <section
        className={`certifications-list-section ${visibleSections.has('certifications') ? 'visible' : ''}`}
        data-section="certifications"
      >
        <div className="certifications-container">
          <div className="section-header centered">
            <div className="section-badge">
              <span className="badge-dot"></span>
              <span>Nos certifications disponibles</span>
            </div>
            <h2 className="section-title">
              Choisissez votre  
              <span className="highlight-text"> certification</span>
            </h2>
          </div>

          {/* Filters */}
          <div className="filters-section">
            <div className="search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Rechercher une certification..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filters-group">
              <div className="filter-item">
                <FaFilter className="filter-icon" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">Toutes les catégories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="filter-item">
                <FaChartLine className="filter-icon" />
                <select
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">Tous les niveaux</option>
                  {levels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="results-count">
              {filteredCertifications.length} certification{filteredCertifications.length > 1 ? 's' : ''}
            </div>
          </div>

          {/* Certifications Grid */}
          {filteredCertifications.length > 0 ? (
            <div className="certifications-grid">
              {filteredCertifications.map((cert, index) => (
                <div key={cert.id} className="certification-card" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="cert-image-wrapper">
                    <img src={cert.image} alt={cert.title} className="cert-image" />
                    <div className="cert-overlay"></div>
                    <div className="cert-badge" style={{ background: cert.color }}>
                      <FaCertificate />
                      {cert.organism}
                    </div>
                  </div>

                  <div className="cert-content">
                    <div className="cert-category" style={{ color: cert.color }}>
                      <FaBook />
                      {cert.category}
                    </div>

                    <h3 className="cert-title">{cert.title}</h3>
                    <p className="cert-description">{cert.description}</p>

                    <div className="cert-info-grid">
                      <div className="cert-info-item">
                        <FaClock />
                        <div>
                          <div className="info-label">Durée</div>
                          <div className="info-value">{cert.duration}</div>
                        </div>
                      </div>

                      <div className="cert-info-item">
                        <FaGlobe />
                        <div>
                          <div className="info-label">Reconnaissance</div>
                          <div className="info-value">{cert.recognition}</div>
                        </div>
                      </div>

                      <div className="cert-info-item">
                        <FaFileAlt />
                        <div>
                          <div className="info-label">Examen</div>
                          <div className="info-value">{cert.examen}</div>
                        </div>
                      </div>

                      <div className="cert-info-item">
                        <FaChartLine />
                        <div>
                          <div className="info-label">Niveau</div>
                          <div className="info-value">{cert.level}</div>
                        </div>
                      </div>
                    </div>

                    <div className="cert-benefits">
                      <h4>Avantages inclus :</h4>
                      <ul>
                        {cert.benefits.slice(0, 3).map((benefit, idx) => (
                          <li key={idx}>
                            <FaCheckCircle />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="cert-footer">
                      <div className="cert-price">
                        <div className="price-label">Prix</div>
                        <div className="price-value">{cert.price}</div>
                      </div>
                      {/* <button className="btn-cert-details">
                        En savoir plus
                        <FaArrowRight />
                      </button> */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <FaSearch className="no-results-icon" />
              <h3>Aucune certification trouvée</h3>
              <p>Essayez de modifier vos critères de recherche</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                  setSelectedLevel('all');
                }}
                className="btn-reset"
              >
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Process Section */}
      <section
        className={`process-section ${visibleSections.has('process') ? 'visible' : ''}`}
        data-section="process"
      >
        <div className="certifications-container">
          <div className="section-header centered">
            <div className="section-badge">
              <span className="badge-dot"></span>
              <span>Comment ça marche</span>
            </div>
            <h2 className="section-title">
              Le processus de 
              <span className="highlight-text"> certification</span>
            </h2>
            <p className="section-description">
              Suivez ces 4 étapes simples pour obtenir votre certification internationale
            </p>
          </div>

          <div className="process-grid">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="process-card">
                  <div className="process-number">{step.number}</div>
                  <div className="process-icon">
                    <Icon />
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                  {index < processSteps.length - 1 && (
                    <div className="process-arrow">→</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="cta-certification">
        <div className="certifications-container">
          <div className="cta-content">
            <div className="cta-icon">
              <FaGraduationCap />
            </div>
            <h2>Prêt à Obtenir Votre Certification ?</h2>
            <p>
              Rejoignez plus de 500 professionnels certifiés et donnez un nouvel 
              élan à votre carrière avec nos certifications internationales reconnues.
            </p>
            <div className="cta-buttons">
              <button className="cta-btn primary">
                Voir toutes les certifications
              </button>
              <button className="cta-btn secondary">
                Contactez-nous
              </button>
            </div>
          </div>
        </div>
      </section> */}

      <style>{styles}</style>
    </div>
    <Footer />
    </>
  );
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .certifications-wrapper {
    font-family: 'Inter', sans-serif;
    background: #fafafa;
  }

  .certifications-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
  }

  /* Animations */
  section {
    opacity: 0;
    transform: translateY(50px);
    transition: opacity 0.8s ease-out, transform 0.8s ease-out;
  }

  section.visible, .certifications-hero, .stats-section {
    opacity: 1;
    transform: translateY(0);
  }

  /* Hero Section */
  .certifications-hero {
  padding: 100px 0 80px;
  text-align: center;
  position: relative;
  overflow: hidden;
  margin-top: 30px;
  background: white; 
  min-height: auto; 
}
  .certifications-hero::before {
  content: '';
  position: absolute;
  top: -20%;
  right: -10%;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(253, 126, 20, 0.08) 0%, transparent 70%);
  border-radius: 50%;
  animation: floatBubble 20s ease-in-out infinite;
}

.certifications-hero::after {
  content: '';
  position: absolute;
  bottom: -20%;
  left: -10%;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(220, 53, 69, 0.08) 0%, transparent 70%);
  border-radius: 50%;
  animation: floatBubble 15s ease-in-out infinite reverse;
}


  .hero-overlay {
  display: none; /* Ou supprimer complètement cette règle */
 }

  .hero-content {
    text-align: center;
    position: relative;
    z-index: 1;
    max-width: 900px;
    margin: 0 auto;
  }

  .hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 10px 24px;
  border-radius: 50px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
  font-size: 14px;
  font-weight: 600;
  color: #fd7e14;
  position: relative;
  z-index: 1;
  border: none; 
  backdrop-filter: none; 
  animation: fadeInDown 0.8s ease-out;
 }

  .badge-icon-spin {
    animation: rotate 3s linear infinite;
  }

  @keyframes rotate {
   from { transform: rotate(0deg); }
   to { transform: rotate(360deg); }
  }


  @keyframes floatBubble {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(30px, -30px) scale(1.1); }
}

  .hero-title {
  font-size: 56px;
  font-weight: 900;
  line-height: 1.15;
  color: #1a202c; 
  margin-bottom: 20px;
  letter-spacing: -1.5px;
  position: relative;
  z-index: 1;
  animation: fadeInUp 0.8s ease-out 0.2s backwards;
 }

 .gradient-text {
  background: linear-gradient(135deg, #fd7e14 0%, #dc3545 50%, #198754 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientShift 5s ease infinite;
  background-size: 200% 200%;
 }
 
  @keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
 }

  .hero-description {
  font-size: 18px;
  line-height: 1.7;
  color: #4a5568; /* Changé de white vers gray */
  margin: 0 auto 32px;
  max-width: 700px;
  position: relative;
  z-index: 1;
  animation: fadeInUp 0.8s ease-out 0.4s backwards;
 }

  .hero-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
  animation: fadeInUp 0.8s ease-out 0.6s backwards;
 }

  .btn-primary-hero,
  .btn-secondary-hero {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 36px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  font-family: inherit;
  position: relative;
  overflow: hidden;
 }

  .btn-primary-hero {
  background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
  color: white;
  box-shadow: 0 8px 24px rgba(253, 126, 20, 0.35);
 }

  .btn-primary-hero:hover {
    transform: translateY(-4px);
  box-shadow: 0 12px 36px rgba(253, 126, 20, 0.45);
 }

  .btn-secondary-hero {
  background: white;
  color: #fd7e14;
  border: 2px solid #fd7e14;
  backdrop-filter: none; /* Supprimer le blur */
 }

  .btn-secondary-hero:hover {
  background: #fd7e14;
  color: white;
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(253, 126, 20, 0.3);
 }

  /* Stats Section */
  .stats-section {
    padding: 60px 0;
    background: white;
    margin-top: -40px;
    position: relative;
    z-index: 2;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }

  .stat-card {
    background: white;
    border-radius: 20px;
    padding: 40px 24px;
    text-align: center;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    border: 2px solid transparent;
  }

  .stat-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
    border-color: var(--stat-color);
  }

  .stat-icon {
    width: 70px;
    height: 70px;
    margin: 0 auto 20px;
    border-radius: 50%;
    background: var(--stat-color);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 32px;
  }

  .stat-value {
    font-size: 40px;
    font-weight: 900;
    color: #1a202c;
    margin-bottom: 8px;
  }

  .stat-label {
    font-size: 15px;
    color: #6b7280;
    font-weight: 600;
  }

  /* Section Header */
  .section-header {
    text-align: left;
    margin-bottom: 60px;
  }

  .section-header.centered {
    text-align: center;
  }

  .section-badge {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: linear-gradient(135deg, #fff5f0 0%, #fef3c7 100%);
    padding: 8px 20px;
    border-radius: 50px;
    margin-bottom: 20px;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }

  .badge-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #fd7e14, #dc3545);
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.3); opacity: 0.7; }
  }

  .section-title {
    font-size: 48px;
    font-weight: 900;
    color: #1a202c;
    margin-bottom: 16px;
    line-height: 1.2;
  }

  .highlight-text {
    background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .section-description {
    font-size: 18px;
    color: #6b7280;
    max-width: 700px;
    margin: 0 auto;
  }

  /* Advantages Section */
  .advantage-card {
    background: white;
    border-radius: 20px;
    padding: 40px 28px;
    text-align: center;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    transition: all 0.4s ease;
    border: 2px solid transparent;
  }

  .advantage-card:hover {
    transform: translateY(-12px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
    border-color: var(--advantage-color);
  }

  .advantage-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 24px;
    border-radius: 20px;
    background: var(--advantage-color);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 36px;
    transition: all 0.3s ease;
  }

  .advantage-card:hover .advantage-icon {
    transform: scale(1.1) rotate(-5deg);
  }

  .advantage-card h3 {
    font-size: 20px;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 12px;
  }

  .advantage-card p {
    font-size: 15px;
    color: #6b7280;
    line-height: 1.7;
  }

  /* Certifications List Section */
  .certifications-list-section {
    padding: 100px 0;
    background: #fafafa;
  }

  /* Filters */
  .filters-section {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    align-items: center;
    margin-bottom: 40px;
    padding: 24px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  }

  .search-box {
    flex: 1;
    min-width: 300px;
    position: relative;
  }

  .search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 18px;
  }

  .search-input {
    width: 100%;
    padding: 14px 16px 14px 48px;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    font-size: 15px;
    font-family: inherit;
    transition: all 0.3s ease;
  }

  .search-input:focus {
    outline: none;
    border-color: #fd7e14;
    box-shadow: 0 0 0 4px rgba(253, 126, 20, 0.1);
  }

  .filters-group {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .filter-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    background: #f9fafb;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    transition: all 0.3s ease;
  }

  .filter-item:focus-within {
    border-color: #fd7e14;
    background: white;
  }

  .filter-icon {
    color: #fd7e14;
    font-size: 16px;
  }

  .filter-select {
    border: none;
    background: transparent;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    cursor: pointer;
    font-family: inherit;
    outline: none;
    min-width: 180px;
  }

  .results-count {
    font-size: 15px;
    font-weight: 700;
    color: #1a202c;
    background: #fff5f0;
    padding: 10px 20px;
    border-radius: 10px;
    border: 2px solid #fd7e14;
  }

  /* Certifications Grid */
  .certifications-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
    gap: 32px;
  }

  .certification-card {
    background: white;
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid transparent;
    animation: fadeInUp 0.6s ease-out backwards;
  }
    @keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
 }

  @keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
 }

  .certification-card:hover {
    transform: translateY(-12px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    border-color: #fd7e14;
  }

  .cert-image-wrapper {
    position: relative;
    height: 220px;
    overflow: hidden;
  }

  .cert-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ease;
  }

  .certification-card:hover .cert-image {
    transform: scale(1.1);
  }

  .cert-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.6) 100%);
  }

  .cert-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 8px;
    color: white;
    font-size: 13px;
    font-weight: 700;
    backdrop-filter: blur(10px);
    z-index: 2;
  }

  .cert-content {
    padding: 28px;
  }

  .cert-category {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 12px;
  }

  .cert-category svg {
    font-size: 12px;
  }

  .cert-title {
    font-size: 22px;
    font-weight: 800;
    color: #1a202c;
    margin-bottom: 12px;
    line-height: 1.3;
  }

  .cert-description {
    font-size: 15px;
    color: #6b7280;
    line-height: 1.6;
    margin-bottom: 20px;
  }

  .cert-info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-bottom: 20px;
    padding: 20px;
    background: #f9fafb;
    border-radius: 12px;
  }

  .cert-info-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .cert-info-item svg {
    color: #fd7e14;
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .info-label {
    font-size: 11px;
    color: #9ca3af;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }

  .info-value {
    font-size: 14px;
    color: #1a202c;
    font-weight: 700;
  }

  .cert-benefits {
    margin-bottom: 24px;
  }

  .cert-benefits h4 {
    font-size: 15px;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 12px;
  }

  .cert-benefits ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .cert-benefits li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #4b5563;
  }

  .cert-benefits svg {
    color: #198754;
    font-size: 14px;
    flex-shrink: 0;
  }

  .cert-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 20px;
    border-top: 2px solid #f3f4f6;
  }

  .cert-price {
  }

  .price-label {
    font-size: 12px;
    color: #9ca3af;
    font-weight: 600;
    margin-bottom: 4px;
  }

  .price-value {
    font-size: 20px;
    font-weight: 900;
    color: #fd7e14;
  }

  .btn-cert-details {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
    box-shadow: 0 4px 12px rgba(253, 126, 20, 0.3);
  }

  .btn-cert-details:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(253, 126, 20, 0.4);
  }

  /* No Results */
  .no-results {
    text-align: center;
    padding: 80px 32px;
    background: white;
    border-radius: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  }

  .no-results-icon {
    font-size: 64px;
    color: #e5e7eb;
    margin-bottom: 24px;
  }

  .no-results h3 {
    font-size: 28px;
    font-weight: 700;
    color: #374151;
    margin-bottom: 12px;
  }

  .no-results p {
    font-size: 16px;
    color: #6b7280;
    margin-bottom: 32px;
  }

  .btn-reset {
    padding: 14px 32px;
    background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
  }

  .btn-reset:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(253, 126, 20, 0.4);
  }

  /* Process Section */
  .process-section {
    padding: 100px 0;
    background: linear-gradient(135deg, #fafafa 0%, #f8f9fa 100%);
  }

  .process-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 32px;
    position: relative;
  }

  .process-card {
    background: white;
    padding: 40px 32px;
    border-radius: 20px;
    text-align: center;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    transition: all 0.4s ease;
    position: relative;
  }

  .process-card:hover {
    transform: translateY(-10px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }

  .process-number {
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 20px;
    font-weight: 900;
    box-shadow: 0 8px 20px rgba(253, 126, 20, 0.3);
  }

  .process-icon {
    width: 70px;
    height: 70px;
    margin: 0 auto 24px;
    background: linear-gradient(135deg, #fff5f0 0%, #fef3c7 100%);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fd7e14;
    font-size: 32px;
    transition: all 0.3s ease;
  }

  .process-card:hover .process-icon {
    transform: scale(1.1) rotate(-5deg);
  }

  .process-card h3 {
    font-size: 20px;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 12px;
  }

  .process-card p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.6;
  }

  .process-arrow {
    position: absolute;
    top: 50%;
    right: -45px;
    font-size: 32px;
    color: #e5e7eb;
    z-index: 1;
  }

  /* CTA Section */
  .cta-certification {
    padding: 100px 0;
    background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
    position: relative;
    overflow: hidden;
  }

  .cta-certification::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(253, 126, 20, 0.15) 0%, transparent 70%);
    border-radius: 50%;
    animation: float 20s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(30px, -30px) scale(1.1); }
  }

  .cta-content {
    text-align: center;
    position: relative;
    z-index: 1;
    max-width: 800px;
    margin: 0 auto;
  }

  .cta-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 28px;
    background: rgba(253, 126, 20, 0.2);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fd7e14;
    font-size: 40px;
  }

  .cta-content h2 {
    font-size: 42px;
    font-weight: 900;
    color: white;
    margin-bottom: 16px;
    line-height: 1.2;
  }

  .cta-content p {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.7;
    margin-bottom: 40px;
  }

  .cta-buttons {
    display: flex;
    gap: 16px;
    justify-content: center;
    flex-wrap: wrap;
  }

  .cta-btn {
    padding: 16px 36px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
    border: none;
  }

  .cta-btn.primary {
    background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
    color: white;
    box-shadow: 0 8px 24px rgba(253, 126, 20, 0.35);
  }

  .cta-btn.primary:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 36px rgba(253, 126, 20, 0.5);
  }

  .cta-btn.secondary {
    background: transparent;
    color: white;
    border: 2px solid white;
  }

  .cta-btn.secondary:hover {
    background: white;
    color: #1a202c;
    transform: translateY(-4px);
  }

  /* Responsive */
  @media (max-width: 1024px) {
    .hero-title {
      font-size: 42px;
    }

    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .advantages-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .certifications-grid {
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    }

    .process-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .process-arrow {
      display: none;
    }
  }

  @media (max-width: 768px) {
    .certifications-hero {
     padding: 80px 0 60px;
    }

    .hero-title {
       font-size: 36px;
    }

    .hero-description {
      font-size: 16px;
    }

    .hero-actions {
      flex-direction: column;
    }

    .btn-primary-hero,
    .btn-secondary-hero {
      width: 100%;
      justify-content: center;
    }

    .stats-grid {
      grid-template-columns: 1fr;
      gap: 16px;
    }

    .advantages-grid {
      grid-template-columns: 1fr;
    }

    .filters-section {
      flex-direction: column;
      align-items: stretch;
    }

    .search-box {
      min-width: 100%;
    }

    .filters-group {
      flex-direction: column;
    }

    .filter-item {
      width: 100%;
    }

    .filter-select {
      width: 100%;
    }

    .certifications-grid {
      grid-template-columns: 1fr;
    }

    .cert-info-grid {
      grid-template-columns: 1fr;
    }

    .cert-footer {
      flex-direction: column;
      gap: 16px;
      align-items: stretch;
    }

    .btn-cert-details {
      width: 100%;
      justify-content: center;
    }

    .process-grid {
      grid-template-columns: 1fr;
    }

    .cta-content h2 {
      font-size: 32px;
    }

    .cta-content p {
      font-size: 16px;
    }

    .cta-buttons {
      flex-direction: column;
    }

    .cta-btn {
      width: 100%;
    }
  }

  @media (max-width: 640px) {
    .certifications-container {
      padding: 0 16px;
    }

    .hero-title {
      font-size: 28px;
    }

    .section-title {
      font-size: 32px;
    }

    .cert-content {
      padding: 20px;
    }

    .cert-title {
      font-size: 18px;
    }
  }
`;

export default Certifications;